import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Empty,
  Input,
  Modal,
  Select,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DeleteOutlined,
  MailOutlined,
  MessageOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  SafetyCertificateOutlined,
  SendOutlined,
  StopOutlined,
  UserOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons';
import {
  ClarificationQuestionSource,
  ClarificationRequest,
} from '../models/clarification.model';
import { ClarificationService } from '../services/clarification.service';
import { CandidateSummaryService } from '../services/candidate-summary.service';
import {
  buildClarificationQuestionSuggestions,
  buildFollowUpQuestionSuggestions,
} from '../utils/clarification-question-suggestions';
import './CandidateClarificationsPanel.css';

interface Props {
  applicationId: string;
  candidateName: string;
  jobTitle: string;
  emailAllowed: boolean;
  whatsappAllowed: boolean;
  focusedRequestId?: string;
}

const statusColor: Record<string, string> = {
  DRAFT: 'default',
  SENT: 'processing',
  ANSWERED: 'success',
  CLOSED: 'default',
  EXPIRED: 'warning',
  CANCELLED: 'default',
};

const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

const hasWaitedThreeDays = (sentAt?: string) =>
  Boolean(sentAt && Date.now() - new Date(sentAt).getTime() >= THREE_DAYS_MS);

const CandidateClarificationsPanel: React.FC<Props> = ({
  applicationId,
  candidateName,
  jobTitle,
  emailAllowed,
  whatsappAllowed,
  focusedRequestId,
}) => {
  const [requests, setRequests] = useState<ClarificationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [reviewingQuestionId, setReviewingQuestionId] = useState<string | null>(
    null
  );
  const [cancellingRequestId, setCancellingRequestId] = useState<string | null>(
    null
  );
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [questions, setQuestions] = useState<
    Array<{ question: string; source: ClarificationQuestionSource }>
  >([{ question: '', source: 'MANUAL' as const }]);
  const [email, setEmail] = useState(emailAllowed);
  const [whatsapp, setWhatsapp] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRequests(await ClarificationService.list(applicationId));
    } catch {
      message.error('Could not load candidate clarifications.');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (loading || !focusedRequestId) return;
    const element = document.getElementById(
      `clarification-request-${focusedRequestId}`
    );
    if (!element) return;
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.focus({ preventScroll: true });
  }, [focusedRequestId, loading, requests]);

  const awaiting = requests.filter(
    (request) => request.status === 'SENT'
  ).length;
  const needsReview = requests.filter(
    (request) => request.status === 'ANSWERED'
  ).length;
  const canSend =
    questions.length > 0 &&
    questions.every((item) => item.question.trim()) &&
    (email || whatsapp);
  const completedQuestions = questions.filter((item) =>
    item.question.trim()
  ).length;
  const deliverySummary = [email ? 'email' : '', whatsapp ? 'WhatsApp' : '']
    .filter(Boolean)
    .join(' and ');

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await ClarificationService.create(applicationId, questions, {
        email,
        whatsapp,
      });
      message.success('Clarification request sent.');
      setComposeOpen(false);
      setQuestions([{ question: '', source: 'MANUAL' }]);
      await load();
    } catch {
      message.error('Could not send the clarification request.');
    } finally {
      setSending(false);
    }
  };

  const selectQuestionSource = async (
    index: number,
    source: ClarificationQuestionSource
  ) => {
    setQuestions((current) =>
      current.map((question, questionIndex) =>
        questionIndex === index ? { ...question, source } : question
      )
    );

    if (source !== 'AI_BRIEF_GAP' && source !== 'AI_BRIEF_INTERVIEW_FOCUS') {
      return;
    }

    setLoadingSuggestions(true);
    try {
      const summary = await CandidateSummaryService.GetSummary(
        applicationId,
        undefined,
        false
      );
      if (!summary) {
        message.info(
          'Generate the AI Brief before adding suggested questions.'
        );
        return;
      }

      const suggestions = buildClarificationQuestionSuggestions(
        summary.payload,
        source
      );
      if (!suggestions.length) {
        message.info(
          source === 'AI_BRIEF_GAP'
            ? 'The AI Brief has no open gaps to clarify.'
            : 'The AI Brief has no interview-focus suggestions.'
        );
        return;
      }

      setQuestions((current) => {
        const preserved = current.filter(
          (question, questionIndex) =>
            questionIndex !== index && question.question.trim()
        );
        return [...preserved, ...suggestions].slice(0, 5);
      });
      message.success(
        `${suggestions.length} suggested question${suggestions.length === 1 ? '' : 's'} added from the AI Brief.`
      );
    } catch {
      message.error('Could not load suggestions from the AI Brief.');
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const reviewQuestion = async (
    requestId: string,
    questionId: string,
    status: 'SUFFICIENT' | 'FOLLOW_UP_NEEDED'
  ) => {
    setReviewingQuestionId(questionId);
    try {
      await ClarificationService.review(
        applicationId,
        requestId,
        questionId,
        status
      );
      if (status === 'FOLLOW_UP_NEEDED') {
        message.info(
          'Marked for follow-up. Mark any other answers, then prepare them together.'
        );
      }
      await load();
    } catch {
      message.error('Could not update the clarification review.');
    } finally {
      setReviewingQuestionId(null);
    }
  };

  const prepareFollowUps = (request: ClarificationRequest) => {
    const followUps = buildFollowUpQuestionSuggestions(request.questions);
    if (!followUps.length) return;
    setQuestions(followUps);
    setComposeOpen(true);
    message.info(
      `${followUps.length} follow-up question${followUps.length === 1 ? '' : 's'} prepared. They will be sent together in one request.`
    );
  };

  const cancelRequest = (request: ClarificationRequest) => {
    Modal.confirm({
      title: 'Cancel clarification request?',
      icon: <StopOutlined />,
      content:
        'This is a recruiter-only action. The candidate will not be notified, but their secure response link will stop working immediately.',
      okText: 'Cancel request',
      okButtonProps: { danger: true },
      cancelText: 'Keep waiting',
      onOk: async () => {
        setCancellingRequestId(request.id);
        try {
          await ClarificationService.cancel(applicationId, request.id);
          message.success('Clarification request cancelled.');
          await load();
        } catch (error) {
          message.error('Could not cancel the clarification request.');
          throw error;
        } finally {
          setCancellingRequestId(null);
        }
      },
    });
  };

  const newestAnswerAt = useMemo(
    () => requests.find((request) => request.status === 'ANSWERED')?.answeredAt,
    [requests]
  );

  return (
    <section
      className="clarifications-panel"
      aria-labelledby="clarifications-heading"
    >
      <header className="clarifications-panel__header">
        <div className="clarifications-panel__identity">
          <span>
            <QuestionCircleOutlined />
          </span>
          <div>
            <Typography.Title level={4} id="clarifications-heading">
              Candidate clarifications
            </Typography.Title>
            <Typography.Text type="secondary">
              Resolve resume questions without changing the hiring stage.
            </Typography.Text>
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setComposeOpen(true)}
        >
          Request clarification
        </Button>
      </header>

      <div className="clarifications-panel__metrics">
        <article>
          <ClockCircleOutlined />
          <span>Awaiting candidate</span>
          <strong>{awaiting}</strong>
        </article>
        <article>
          <MessageOutlined />
          <span>Needs review</span>
          <strong>{needsReview}</strong>
        </article>
        <article>
          <CheckCircleOutlined />
          <span>Total requests</span>
          <strong>{requests.length}</strong>
        </article>
      </div>

      {newestAnswerAt ? (
        <Alert
          type="info"
          showIcon
          message="New clarification evidence available"
          description="Review the candidate’s answers before regenerating the AI Brief. The recommendation is never changed automatically."
          className="clarifications-panel__alert"
        />
      ) : null}

      {loading ? (
        <Skeleton active paragraph={{ rows: 4 }} />
      ) : requests.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No clarification requests yet. Ask focused questions when resume evidence needs validation."
        />
      ) : (
        <div className="clarifications-list">
          {requests.map((request) => {
            const followUpCount = request.questions.filter(
              (question) => question.reviewStatus === 'FOLLOW_UP_NEEDED'
            ).length;
            const overdueResponse =
              request.status === 'SENT' && hasWaitedThreeDays(request.sentAt);
            return (
              <article
                id={`clarification-request-${request.id}`}
                className={`clarification-card ${
                  focusedRequestId === request.id
                    ? 'clarification-card--focused'
                    : ''
                }`}
                key={request.id}
                tabIndex={focusedRequestId === request.id ? -1 : undefined}
              >
                <div className="clarification-card__top">
                  <div>
                    <Tag color={statusColor[request.status]}>
                      {request.status.replace(/_/g, ' ')}
                    </Tag>
                    <strong>
                      {request.questions.length} question
                      {request.questions.length === 1 ? '' : 's'}
                    </strong>
                  </div>
                  <span>
                    {request.sentAt
                      ? new Date(request.sentAt).toLocaleDateString()
                      : 'Draft'}
                  </span>
                </div>
                <div className="clarification-card__questions">
                  {request.questions.map((question, index) => (
                    <div key={question.id} className="clarification-question">
                      <span>{index + 1}</span>
                      <div>
                        <strong>{question.question}</strong>
                        {question.answer ? (
                          <p>{question.answer}</p>
                        ) : (
                          <em>Waiting for candidate response</em>
                        )}
                      </div>
                      {question.answer ? (
                        <Select
                          size="small"
                          value={question.reviewStatus}
                          loading={reviewingQuestionId === question.id}
                          disabled={reviewingQuestionId !== null}
                          onChange={(status) => {
                            if (status === 'OPEN') return;
                            void reviewQuestion(
                              request.id,
                              question.id,
                              status
                            );
                          }}
                          options={[
                            {
                              value: 'OPEN',
                              label: 'Needs review',
                              disabled: true,
                            },
                            { value: 'SUFFICIENT', label: 'Sufficient' },
                            {
                              value: 'FOLLOW_UP_NEEDED',
                              label: 'Follow-up needed',
                            },
                          ]}
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
                <footer>
                  <Space wrap>
                    {request.emailDelivered ? (
                      <Tag icon={<MailOutlined />}>Email sent</Tag>
                    ) : null}
                    {request.whatsappDelivered ? (
                      <Tag icon={<WhatsAppOutlined />}>WhatsApp sent</Tag>
                    ) : null}
                    {overdueResponse ? (
                      <Tag color="warning" icon={<ClockCircleOutlined />}>
                        No response for 3+ days
                      </Tag>
                    ) : null}
                  </Space>
                  {request.status === 'SENT' ? (
                    <Button
                      size="small"
                      danger
                      icon={<StopOutlined />}
                      loading={cancellingRequestId === request.id}
                      disabled={cancellingRequestId !== null}
                      onClick={() => cancelRequest(request)}
                    >
                      Cancel request
                    </Button>
                  ) : request.status === 'ANSWERED' ? (
                    <Space wrap>
                      {followUpCount ? (
                        <Button
                          size="small"
                          type="primary"
                          icon={<MessageOutlined />}
                          onClick={() => prepareFollowUps(request)}
                        >
                          Prepare {followUpCount} follow-up question
                          {followUpCount === 1 ? '' : 's'}
                        </Button>
                      ) : null}
                      <Button
                        size="small"
                        onClick={async () => {
                          await ClarificationService.close(
                            applicationId,
                            request.id
                          );
                          await load();
                        }}
                      >
                        Close request
                      </Button>
                    </Space>
                  ) : null}
                </footer>
              </article>
            );
          })}
        </div>
      )}

      <Modal
        className="clarification-compose-modal"
        open={composeOpen}
        onCancel={() => setComposeOpen(false)}
        title={
          <div className="clarification-compose__title">
            <span>
              <QuestionCircleOutlined />
            </span>
            <div>
              <strong>Request candidate clarification</strong>
              <small>Send a focused, secure set of questions</small>
            </div>
          </div>
        }
        width={720}
        footer={
          <div className="clarification-compose__footer">
            <span>
              {completedQuestions !== questions.length
                ? 'Complete every question to continue.'
                : !deliverySummary
                  ? 'Select at least one delivery method.'
                  : `${questions.length} question${questions.length === 1 ? '' : 's'} ready to send via ${deliverySummary}.`}
            </span>
            <Space>
              <Button onClick={() => setComposeOpen(false)}>Cancel</Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                loading={sending}
                disabled={!canSend}
                onClick={() => void send()}
              >
                Send request
              </Button>
            </Space>
          </div>
        }
      >
        <div className="clarification-compose__context">
          <span className="clarification-compose__avatar">
            <UserOutlined />
          </span>
          <div>
            <small>REQUEST FOR</small>
            <strong>{candidateName}</strong>
            <span>{jobTitle}</span>
          </div>
        </div>
        <div className="clarification-compose__notice">
          <SafetyCertificateOutlined />
          <span>
            <strong>Private response link</strong>
            The candidate receives one notification per selected delivery
            method, with a single secure link for all questions. The link
            expires in 7 days.
          </span>
        </div>
        <div className="clarification-compose__section-heading">
          <div>
            <strong>Questions</strong>
            <span>Ask only what is needed to validate resume evidence.</span>
          </div>
          <span>{questions.length} of 5</span>
        </div>
        <div className="clarification-compose__questions">
          {questions.map((item, index) => (
            <div className="clarification-compose__question" key={index}>
              <div className="clarification-compose__question-head">
                <strong>Question {index + 1}</strong>
                <Select
                  aria-label={`Evidence source for question ${index + 1}`}
                  loading={loadingSuggestions}
                  disabled={loadingSuggestions}
                  value={item.source}
                  onChange={(source: ClarificationQuestionSource) =>
                    void selectQuestionSource(index, source)
                  }
                  options={[
                    { value: 'MANUAL', label: 'Manual question' },
                    { value: 'AI_BRIEF_GAP', label: 'AI Brief gap' },
                    {
                      value: 'AI_BRIEF_INTERVIEW_FOCUS',
                      label: 'Interview focus',
                    },
                    { value: 'RESUME', label: 'Resume evidence' },
                  ]}
                />
                {questions.length > 1 ? (
                  <Button
                    type="text"
                    danger
                    aria-label={`Remove question ${index + 1}`}
                    icon={<DeleteOutlined />}
                    onClick={() =>
                      setQuestions((current) =>
                        current.filter((_, i) => i !== index)
                      )
                    }
                  />
                ) : null}
              </div>
              <div className="clarification-compose__question-body">
                <Input.TextArea
                  aria-label={`Question ${index + 1}`}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  maxLength={1200}
                  showCount
                  placeholder="Example: Could you describe your production experience with Kubernetes?"
                  value={item.question}
                  onChange={(event) =>
                    setQuestions((current) =>
                      current.map((q, i) =>
                        i === index ? { ...q, question: event.target.value } : q
                      )
                    )
                  }
                />
              </div>
            </div>
          ))}
        </div>
        {questions.length < 5 ? (
          <Button
            className="clarification-compose__add"
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={() =>
              setQuestions((current) => [
                ...current,
                { question: '', source: 'MANUAL' },
              ])
            }
          >
            Add question
          </Button>
        ) : null}
        <div className="clarification-compose__suggestion-help">
          <QuestionCircleOutlined />
          <span>
            Choose <strong>AI Brief gap</strong> or{' '}
            <strong>Interview focus</strong> to add suggested questions
            automatically. You can edit or delete every suggestion.
          </span>
        </div>
        <div className="clarification-compose__delivery-heading">
          <strong>Delivery method</strong>
          <span>Select where the secure response link should be sent.</span>
        </div>
        <div className="clarification-compose__delivery">
          <label
            className={`clarification-compose__channel ${email ? 'is-selected' : ''} ${!emailAllowed ? 'is-disabled' : ''}`}
          >
            <Checkbox
              checked={email}
              disabled={!emailAllowed}
              onChange={(event) => setEmail(event.target.checked)}
            />
            <span className="clarification-compose__channel-icon">
              <MailOutlined />
            </span>
            <span>
              <strong>Email</strong>
              <small>
                {emailAllowed
                  ? 'Send to the candidate’s email address'
                  : 'No candidate email available'}
              </small>
            </span>
          </label>
          <label
            className={`clarification-compose__channel clarification-compose__channel--whatsapp ${whatsapp ? 'is-selected' : ''} ${!whatsappAllowed ? 'is-disabled' : ''}`}
          >
            <Checkbox
              checked={whatsapp}
              disabled={!whatsappAllowed}
              onChange={(event) => setWhatsapp(event.target.checked)}
            />
            <span className="clarification-compose__channel-icon">
              <WhatsAppOutlined />
            </span>
            <span>
              <strong>WhatsApp</strong>
              <small>
                {whatsappAllowed
                  ? 'Send the same secure response link'
                  : 'No candidate phone available'}
              </small>
            </span>
          </label>
        </div>
      </Modal>
    </section>
  );
};

export default CandidateClarificationsPanel;
